import { useContext } from 'react'

import useToasterHelper from '../helpers/ToasterHelper'
import { convertPrice } from '../helpers/price'
import { addSalesTax, getPaymentMethodsFormatted } from '../helpers/payment'
import { getRegistrationEventDataInDb } from '../helpers/registration-on-event'
import { getUserFullName } from '../helpers/helpers'
import { getScratchItems } from '../helpers/scratch'

import { useAppSelector } from '../store/hooks'
import { selectProfileData } from '../store/user/userSlice'

import { HandleModalContext } from '../layout/mainlayout/MainLayout'
import { httpService } from '../services/httpService'
import EventRegisteredUsersService from '../services/firebase-service/event-registered-users/EventRegisteredUsersService'

import {
  IOnPayload,
  IPaymentType,
  IRefundPayload,
  IRefundPaymentInfo,
  IScratchPayload,
  IUndoOnPayload,
  PAYMENT_STATUS,
  PAYMENT_TYPE,
} from '../types/payment'
import { IRegistrationByDayInterface } from '../models/registrations-by-day/registrationByDay.interface'
import { IEventReviewPublish } from '../models/event-review-publish/event-review-publish.interface'
import { IRegistrationFeesInterface } from '../models/registration-fees/registrationFees.interface'
import { IRegistrationTicketInterface } from '../models/registration-tickets/registrationTicket.interface'
import {
  IPaymentsCards,
  ISelectedCard,
  IUserACH,
  IUserCard,
  IUserInterface,
} from '../models/users/user.interface'

import { MESSAGES_CONST } from '../const/messages-const'
import { MODAL_CONSTS } from '../const/modal-const'

export const usePaymentActions = () => {
  const toastFunctions = useToasterHelper()

  const profileData = useAppSelector(selectProfileData)

  const handleModalContext = useContext(HandleModalContext)

  const onRefund = async ({
    refundRegistrationsByDayItems,
    refundFeesItems,
    refundTicketsItems,
    registrationDocId,
    ownerId,
    publishedEvent,
    // refund value entered by the organizer
    refundValue,
  }: {
    refundRegistrationsByDayItems: IRegistrationByDayInterface[]
    refundFeesItems: IRegistrationFeesInterface[]
    refundTicketsItems: IRegistrationTicketInterface[]
    registrationDocId: string
    ownerId: string
    publishedEvent?: IEventReviewPublish | null
    refundValue?: number
  }) => {
    try {
      const paymentInfo: IRefundPaymentInfo[] = []

      const refundItems = [
        ...refundRegistrationsByDayItems,
        ...refundFeesItems,
        ...refundTicketsItems,
      ]

      refundItems.forEach((item) => {
        const isExist = paymentInfo?.find((payment) => payment.chargeId === item.invoiceId)
        if (item.invoiceId && item.invoiceUrl && item.recipientId && !isExist) {
          paymentInfo.push({
            chargeId: item.invoiceId,
            invoiceUrl: item.invoiceUrl,
            recipientId: item.recipientId,
          })
        }
      })

      const payloadData: IRefundPayload = {
        scratchedFeesItems:
          refundFeesItems?.map((item) => {
            let amountToRefund
            if (refundValue) amountToRefund = convertPrice(refundValue)
            else amountToRefund = convertPrice(item.feesPrice)

            return {
              docId: item.id || '',
              amountToRefund: amountToRefund / 100,
              invoiceId: item.invoiceId || '',
              salesTax:
                addSalesTax(
                  item.created,
                  amountToRefund,
                  item.includeSalesTax,
                  publishedEvent?.EventPaymentSettings?.chargeSalesTax || false
                ) / 100,
            }
          }) || [],
        scratchedTicketItems:
          refundTicketsItems?.map((item) => {
            let amountToRefund
            if (refundValue) amountToRefund = convertPrice(refundValue)
            else amountToRefund = convertPrice(item.ticketPrice)

            return {
              docId: item.id || '',
              amountToRefund: amountToRefund / 100,
              invoiceId: item.invoiceId || '',
              salesTax:
                addSalesTax(
                  item.created,
                  amountToRefund,
                  item.includeSalesTax,
                  publishedEvent?.EventPaymentSettings?.chargeSalesTax || false
                ) / 100,
            }
          }) || [],
        scratchedRegistrationByDayItems:
          refundRegistrationsByDayItems?.map((item) => {
            let amountToRefund
            if (refundValue) amountToRefund = convertPrice(refundValue)
            else
              amountToRefund =
                convertPrice(item.registrationPrice) +
                (item.isQualify ? convertPrice(item.qualifyFee) : 0)
            return {
              docId: item.id || '',
              amountToRefund: amountToRefund / 100,
              invoiceId: item.invoiceId || '',
              salesTax:
                addSalesTax(
                  item.created,
                  amountToRefund,
                  item.includeSalesTax,
                  publishedEvent?.EventPaymentSettings?.chargeSalesTax || false
                ) / 100,
            }
          }) || [],
        comment: '',
        reason: 'requested_by_customer',
        paymentInfo: paymentInfo,
        registrationDocId: registrationDocId,
        eventOwnerId: publishedEvent?.owner || '',
      }

      await httpService({
        url: 'refund_items_amount',
        data: payloadData,
        method: 'POST',
      })

      const type: ('classes' | 'fees' | 'tickets')[] = []
      if (refundRegistrationsByDayItems?.[0]) type.push('classes')
      if (refundFeesItems?.[0]) type.push('fees')
      if (refundTicketsItems?.[0]) type.push('tickets')

      const { registrationsByDay, registrationsFees, registrationsTickets } =
        await getRegistrationEventDataInDb({
          registrationId: registrationDocId,
          ownerId: ownerId,
          type: type,
        })

      toastFunctions.success({
        message: `${refundItems?.length} items refunded successfully`,
      })

      return {
        registrationsByDayUpdated: registrationsByDay,
        registrationsFeesUpdated: registrationsFees,
        registrationsTicketsUpdated: registrationsTickets,
      }
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.response?.data?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onPayByCard = async ({
    registrationsByDayItems,
    feeItems,
    ticketItems,
    card,
    registrationDocId,
    user,
    publishedEvent,
  }: {
    registrationsByDayItems: IRegistrationByDayInterface[]
    feeItems: IRegistrationFeesInterface[]
    ticketItems: IRegistrationTicketInterface[]
    card: ISelectedCard
    registrationDocId: string
    user: IUserInterface
    publishedEvent?: IEventReviewPublish | null
  }) => {
    try {
      const eventRegisteredUser = await EventRegisteredUsersService.getById(registrationDocId)
      let amountToPay = 0

      registrationsByDayItems.forEach((item) => {
        let amount = 0
        if (item.isQualify) amount += convertPrice(item.qualifyFee)
        if (item.registrationPrice) amount += convertPrice(item.registrationPrice)
        amountToPay +=
          amount +
          addSalesTax(
            item.created,
            amount,
            item.includeSalesTax,
            publishedEvent?.EventPaymentSettings?.chargeSalesTax
          )
      })

      ticketItems.forEach((item) => {
        let amount = 0
        if (item.ticketPrice) amount += convertPrice(item.ticketPrice)
        amountToPay +=
          amount +
          addSalesTax(
            item.created,
            amount,
            item.includeSalesTax,
            publishedEvent?.EventPaymentSettings?.chargeSalesTax
          )
      })

      feeItems.forEach((item) => {
        let amount = 0
        if (item.feesPrice) amount = convertPrice(item.feesPrice)
        amountToPay +=
          amount +
          addSalesTax(
            item.created,
            amount,
            item.includeSalesTax,
            publishedEvent?.EventPaymentSettings?.chargeSalesTax
          )
      })

      const recipient = publishedEvent?.owner === profileData.id ? user : profileData

      const payloadData: IOnPayload = {
        paymentStatus: PAYMENT_STATUS.PAID,
        paymentType: PAYMENT_TYPE.CARD,
        amountToPay: amountToPay / 100,
        pmId: card.pmId,
        cardNumber: card.cardNumber,
        description: 'Event Registration',
        type: 'event_register',
        registrationId: eventRegisteredUser?.id,
        registrationsByDay: registrationsByDayItems
          .filter((item) => !item.isDivision)
          .map((item) => ({
            ...item,
            recipientId: recipient?.id || null,
            recipientName: recipient ? getUserFullName(recipient) : null,
            recipientProfilePicture: user?.userProfilePicture || null,
          })),
        tickets: ticketItems.map((item) => ({
          ...item,
          recipientId: recipient?.id || null,
          recipientName: recipient ? getUserFullName(recipient) : null,
          recipientProfilePicture: recipient?.userProfilePicture || null,
        })),
        fees: feeItems.map((item) => ({
          ...item,
          recipientId: recipient?.id || null,
          recipientName: recipient ? getUserFullName(recipient) : null,
          recipientProfilePicture: recipient?.userProfilePicture || null,
        })),
        ridersTeamMembers: [],
        payerId: recipient?.id,
        registrarId: eventRegisteredUser?.userId,
        eventId: eventRegisteredUser?.eventId,
        isTicketPayment: false,
      }

      const res = await httpService({
        url: 'on_pay',
        method: 'POST',
        data: payloadData,
      })

      toastFunctions.success({
        message: `Paid successfully`,
      })

      return {
        registrationsByDayUpdated: res?.registrationsByDayUpdated,
        registrationsFeesUpdated: res?.feesUpdated,
        registrationsTicketsUpdated: res?.ticketsUpdated,
      }
    } catch (error: any) {
      console.error(error, 'error')
      if (error?.response?.data?.response?.includes('requirements.disabled_reason')) {
        toastFunctions.error({
          message:
            'Error - the event organizers bank account is not connected properly. Contact them or Pegasus to fix it so payments can resume',
        })
      } else {
        toastFunctions.error({
          message: error?.response?.data?.response ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
        })
      }
    }
  }

  const onMarkPaid = async ({
    markPaidRegistrationsByDayItems,
    markPaidFeesItems,
    markPaidTicketsItems,
    paymentType,
    // checkMemo,
    // checkNumber,
    registrationDocId,
    user,
    organizerId,
  }: {
    markPaidRegistrationsByDayItems: IRegistrationByDayInterface[]
    markPaidFeesItems: IRegistrationFeesInterface[]
    markPaidTicketsItems: IRegistrationTicketInterface[]
    paymentType: IPaymentType
    checkMemo?: string
    checkNumber?: string
    registrationDocId: string
    user: IUserInterface
    organizerId: string | null
  }) => {
    try {
      const eventRegisteredUser = await EventRegisteredUsersService.getById(registrationDocId)

      const recipient = organizerId === profileData.id ? user : profileData

      const payloadData: IOnPayload = {
        paymentStatus: PAYMENT_STATUS.PAID,
        paymentType: paymentType,
        // checkNumber: checkNumber || null,
        // checkMemo: checkMemo || null,
        amountToPay: 0,
        pmId: null,
        cardNumber: null,
        description: 'Event Registration',
        type: 'event_register',
        registrationId: eventRegisteredUser?.id,
        registrationsByDay: markPaidRegistrationsByDayItems.map((item) => ({
          ...item,
          recipientId: recipient?.id || null,
          recipientName: recipient ? getUserFullName(recipient) : null,
          recipientProfilePicture: recipient?.userProfilePicture || null,
        })),
        tickets: markPaidTicketsItems.map((item) => ({
          ...item,
          recipientId: recipient?.id || null,
          recipientName: recipient ? getUserFullName(recipient) : null,
          recipientProfilePicture: recipient?.userProfilePicture || null,
        })),
        fees: markPaidFeesItems.map((item) => ({
          ...item,
          recipientId: recipient?.id || null,
          recipientName: recipient ? getUserFullName(recipient) : null,
          recipientProfilePicture: recipient?.userProfilePicture || null,
        })),
        ridersTeamMembers: [],
        payerId: recipient?.id,
        registrarId: eventRegisteredUser?.userId,
        eventId: eventRegisteredUser?.eventId,
        isTicketPayment: false,
      }

      const res = await httpService({
        url: 'on_pay',
        method: 'POST',
        data: payloadData,
      })

      toastFunctions.success({
        message: `Marked paid by ${paymentType} successfully`,
      })

      return {
        registrationsByDayUpdated: res?.registrationsByDayUpdated,
        registrationsFeesUpdated: res?.feesUpdated,
        registrationsTicketsUpdated: res?.ticketsUpdated,
      }
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.response?.data?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onUndoMarkMaid = async ({
    paymentType,
    eventId,
    registrationId,
    registrationsByDay,
    tickets,
    fees,
    publishedEvent,
    user,
  }: {
    eventId: string
    registrationId: string
    paymentType: IPaymentType
    registrationsByDay: IRegistrationByDayInterface[]
    fees: IRegistrationFeesInterface[]
    tickets: IRegistrationTicketInterface[]
    publishedEvent?: IEventReviewPublish | null
    user: IUserInterface
  }) => {
    const recipient = publishedEvent?.owner === profileData.id ? user : profileData

    try {
      const payloadData: IUndoOnPayload = {
        paymentType: paymentType,
        eventId: eventId,
        payerId: recipient.id,
        registrationId: registrationId,
        registrationsByDay: registrationsByDay,
        tickets: tickets,
        fees: fees,
      }

      const res = await httpService({
        url: 'undo_on_pay',
        method: 'POST',
        data: payloadData,
      })

      toastFunctions.success({
        message: 'Undo completed successfully.',
      })

      return {
        registrationsByDayUpdated: res?.registrationsByDayUpdated,
        registrationsFeesUpdated: res?.feesUpdated,
        registrationsTicketsUpdated: res?.ticketsUpdated,
      }
    } catch (error: any) {
      console.error('error:  ', error)
      toastFunctions.error({
        message: error?.response?.data?.error ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onRemoveCard = async ({
    paymentsCards,
    setPaymentsCards,
    organizer,
    cartPmId,
  }: {
    paymentsCards: IPaymentsCards
    setPaymentsCards: (cards: IPaymentsCards) => void
    organizer: IUserInterface | null
    cartPmId: string | null
  }) => {
    try {
      await httpService({
        url: `remove_card`,
        method: 'POST',
        data: {
          pmId: cartPmId,
          userStripeAccountId: organizer?.userStripeAccountId,
        },
      })
      const cards = paymentsCards.CARDS.filter((item) => item.pmId !== cartPmId)
      const ach = paymentsCards.ACH.filter((item) => item.pmId !== cartPmId)
      setPaymentsCards({
        CARDS: cards,
        ACH: ach,
      })
      toastFunctions.success({
        message: 'Card removed successfully',
      })
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.response?.data?.message || MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onAddNewCard = ({
    organizer,
    user,
    setPaymentsCards,
  }: {
    organizer: IUserInterface | null
    user: IUserInterface | null
    setPaymentsCards: (cards: IPaymentsCards) => void
  }) => {
    const recipient = organizer?.id === profileData.id ? user : profileData

    handleModalContext?.handleModal(true, MODAL_CONSTS.CARD, {
      ...handleModalContext?.dataToPassOn,
      add_card: true,
      organizerId: organizer?.id,
      isApproved: profileData?.id === organizer?.id,
      user: recipient,
      onSave: async () => {
        if (recipient?.id) {
          const payments = await getPaymentMethodsFormatted(
            recipient?.userEmail,
            organizer?.userStripeAccountId
          )
          setPaymentsCards(payments)
        }
      },
    })
  }

  const onAddNewBank = ({
    organizer,
    user,
    setPaymentsCards,
  }: {
    organizer: IUserInterface | null
    user: IUserInterface | null
    setPaymentsCards: (cards: IPaymentsCards) => void
  }) => {
    const recipient = organizer?.id === profileData.id ? user : profileData

    handleModalContext?.handleModal(true, MODAL_CONSTS.CONNECT_BANK_ACCOUNT, {
      ...handleModalContext?.dataToPassOn,
      user: recipient,
      organizer: organizer,
      isApproved: profileData?.id === organizer?.id,
      onSave: async () => {
        if (recipient?.id) {
          const payments = await getPaymentMethodsFormatted(
            recipient?.userEmail,
            organizer?.userStripeAccountId
          )
          setPaymentsCards(payments)
        }
      },
    })
  }

  const onRecipientClick = ({
    eventId,
    registrarId,
    registrationId,
    defaultActiveTab,
    defaultOpenedPrimaryAccordion,
    defaultOpenedSecondaryAccordion,
  }: {
    eventId: string
    registrarId: string | null
    registrationId: string
    defaultActiveTab: string
    defaultOpenedPrimaryAccordion: string | null
    defaultOpenedSecondaryAccordion: 'classes' | 'fees' | 'tickets'
  }) => {
    handleModalContext?.handleModal?.(true, MODAL_CONSTS.REGISTRAR_INFO, {
      ...handleModalContext?.dataToPassOn,
      eventId: eventId,
      registrarId: registrarId,
      registrationId: registrationId,
      defaultActiveTab: defaultActiveTab,
      defaultOpenedPrimaryAccordion: defaultOpenedPrimaryAccordion,
      defaultOpenedSecondaryAccordion: defaultOpenedSecondaryAccordion,
    })
  }

  const onSendPaymentRequest = async (
    card: IUserCard | IUserACH,
    user: IUserInterface | null,
    organizer: IUserInterface | null
  ) => {
    try {
      if (user?.id && card?.pmId && organizer?.id) {
        await httpService({
          url: 'send_request_payment',
          method: 'POST',
          data: {
            pmId: card.pmId,
            cardNumber: card.cardNumber,
            requesterId: organizer.id,
            ownerId: user.id,
          },
        })

        toastFunctions.success({
          message: `Request sent successfully to ${getUserFullName(user)}`,
        })
      }
    } catch (error: any) {
      console.error('error: ', error)
      toastFunctions.error({
        message: error?.response?.data?.error ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onScratch = async ({
    registrationDocId,
    user,
    publishedEvent,
    registrationsByDayScratchedItems,
    feesScratchedItems,
    ticketsScratchedItems,
    isScratched,
  }: {
    registrationDocId: string
    publishedEvent?: IEventReviewPublish | null
    user: IUserInterface | null
    registrationsByDayScratchedItems: IRegistrationByDayInterface[]
    feesScratchedItems: IRegistrationFeesInterface[]
    ticketsScratchedItems: IRegistrationTicketInterface[]
    isScratched: boolean
  }) => {
    try {
      const items = [
        ...registrationsByDayScratchedItems,
        ...feesScratchedItems,
        ...ticketsScratchedItems,
      ]

      const { scratchedRegistrations, scratchedFees, scratchedTickets } = getScratchItems({
        registrationsByDay: registrationsByDayScratchedItems,
        fees: feesScratchedItems,
        tickets: ticketsScratchedItems,
        chargeSalesTax: publishedEvent?.EventPaymentSettings?.chargeSalesTax ?? false,
      })

      const payloadData: IScratchPayload = {
        registrationDocId: registrationDocId,
        scratchedRegistrationByDayItems: scratchedRegistrations.scratchedItems,
        scratchedFeesItems: scratchedFees.scratchedItems,
        scratchedTicketItems: scratchedTickets.scratchedItems,
        isChargeSalesTax: publishedEvent?.EventPaymentSettings?.chargeSalesTax ?? false,
        isAlreadyScratched: isScratched,
      }

      await httpService({
        url: 'scratch_items',
        method: 'POST',
        data: payloadData,
      })

      const type: ('classes' | 'fees' | 'tickets')[] = []
      if (registrationsByDayScratchedItems?.[0]) type.push('classes')
      if (feesScratchedItems?.[0]) type.push('fees')
      if (ticketsScratchedItems?.[0]) type.push('tickets')

      const { registrationsByDay, registrationsFees, registrationsTickets } =
        await getRegistrationEventDataInDb({
          registrationId: registrationDocId,
          ownerId: user?.id,
          type: type,
        })

      toastFunctions.success({
        message: `${items.length} ${isScratched ? 'unscratched' : 'scratched'} successfully`,
      })

      return {
        registrationsByDayUpdated: registrationsByDay,
        registrationsFeesUpdated: registrationsFees,
        registrationsTicketsUpdated: registrationsTickets,
      }
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.response?.data?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  return {
    onRefund,
    onPayByCard,
    onMarkPaid,
    onRemoveCard,
    onAddNewCard,
    onAddNewBank,
    onRecipientClick,
    onSendPaymentRequest,
    onUndoMarkMaid,
    onScratch,
  }
}
